import React from 'react';

import { client } from '~/api/client';
import {
  AddCaraWorkspaceMessageDocument,
  CaraWorkspaceState,
  type AddCaraWorkspaceMessageMutation,
  type AddCaraWorkspaceMessageMutationVariables,
  type CaraWorkspaceFieldsFragment
} from '~/api/graphql';
import { newestArtifactVersion } from '~/components/workspaces/lib/artifact';

/** Why sending and redrafting wait while Cara rewrites the email from a template. */
export const WAIT_FOR_CARA_REASON = 'Wait for Cara to finish the draft.';

export type RedraftRequest =
  | { kind: 'template'; templateId: string; templateName: string }
  | { kind: 'latestVersion'; templateId: string; templateName: string }
  | { kind: 'withoutTemplate' };

/**
 * What the agent asks Cara in the chat. The subject says which email is meant, since a
 * workspace can hold several drafts.
 */
export const redraftMessage = (subject: string, request: RedraftRequest) => {
  const trimmedSubject = subject.trim();
  const email = trimmedSubject ? `this email ("${trimmedSubject}")` : 'this email';
  switch (request.kind) {
    case 'template':
      return `Redraft ${email} with the ${request.templateName} template.`;
    case 'latestVersion':
      return `Redraft ${email} with the latest version of the ${request.templateName} template.`;
    case 'withoutTemplate':
      return `Redraft ${email} without a template.`;
  }
};

/** What the composer says while Cara redrafts the email. */
export const redraftingNotice = (request: RedraftRequest) =>
  request.kind === 'withoutTemplate'
    ? 'Cara is rewriting the email…'
    : `Cara is writing the email with the ${request.templateName} template…`;

type Redraft = { notice: string; hasCaraStarted: boolean };

/** One redraft request: the chat message, what it asks for, and the draft it means. */
export type RedraftAsk = { message: string; request: RedraftRequest; emailDraftArtifactId: string };

/**
 * Asks Cara, by a chat message, to redraft the email from a template. Cara rewrites the open
 * email in place through the workspace's usual turn, so there is no second drafting path.
 * `writingNotice` is set from the ask until that turn ends.
 */
export const useTemplateRedraft = (workspaceId: string, workspaceState: CaraWorkspaceState) => {
  const [redraft, setRedraft] = React.useState<Redraft | null>(null);
  const [lastAsk, setLastAsk] = React.useState<RedraftAsk>();
  const [hasFailed, setHasFailed] = React.useState(false);

  const isCaraWorking = workspaceState === CaraWorkspaceState.Working;
  React.useEffect(() => {
    if (!redraft) return;
    if (isCaraWorking && !redraft.hasCaraStarted) setRedraft({ ...redraft, hasCaraStarted: true });
    if (!isCaraWorking && redraft.hasCaraStarted) setRedraft(null);
  }, [isCaraWorking, redraft]);

  const ask = React.useCallback(
    async (redraftAsk: RedraftAsk) => {
      const { message, request, emailDraftArtifactId } = redraftAsk;
      // Sent beside the message so Cara reads the picked template by its ID, not its name.
      const emailTemplateId = request.kind === 'withoutTemplate' ? undefined : request.templateId;
      setLastAsk(redraftAsk);
      setHasFailed(false);
      // Set before the request returns, which can be after Cara's turn has already ended.
      setRedraft({ notice: redraftingNotice(request), hasCaraStarted: false });
      try {
        const result = await client.mutate<
          AddCaraWorkspaceMessageMutation,
          AddCaraWorkspaceMessageMutationVariables
        >({
          mutation: AddCaraWorkspaceMessageDocument,
          variables: {
            id: workspaceId,
            input: { message, files: [], emailDraftArtifactId, emailTemplateId }
          }
        });
        if (result.errors?.length) {
          setRedraft(null);
          setHasFailed(true);
        }
      } catch {
        setRedraft(null);
        setHasFailed(true);
      }
    },
    [workspaceId]
  );

  const retry = () => (lastAsk ? ask(lastAsk) : Promise.resolve());

  return { ask, retry, hasFailed, writingNotice: redraft?.notice ?? null };
};

/**
 * A template redraft under way and the template version the agent chose to keep. The composer
 * remounts for each version Cara writes, so these live above it, for as long as the email is open.
 */
export const useEmailTemplateSession = (
  workspaceId: string,
  workspaceState: CaraWorkspaceState
) => {
  const redraft = useTemplateRedraft(workspaceId, workspaceState);
  const [keptTemplateVersion, setKeptTemplateVersion] = React.useState<number | null>(null);
  return { redraft, keptTemplateVersion, keepTemplateVersion: setKeptTemplateVersion };
};

export type EmailTemplateSession = ReturnType<typeof useEmailTemplateSession>;

type WorkspaceArtifact = CaraWorkspaceFieldsFragment['artifacts'][0];

/**
 * Moves the composer onto the version Cara writes for a redraft. The rail otherwise stays on the
 * version being edited when a newer one lands.
 */
export const useShowRedraftedVersion = (params: {
  session: EmailTemplateSession;
  workspace: CaraWorkspaceFieldsFragment;
  artifact: { id: string; version: number };
  showVersion?: (artifact: WorkspaceArtifact) => void;
}) => {
  const { session, workspace, artifact, showVersion } = params;
  const isRedrafting = session.redraft.writingNotice !== null;
  const newest = newestArtifactVersion(workspace.artifacts, artifact.id);
  const redraftedVersion =
    isRedrafting && newest && newest.version > artifact.version ? newest : undefined;
  React.useEffect(() => {
    if (redraftedVersion) showVersion?.(redraftedVersion);
  }, [redraftedVersion, showVersion]);
};
